import React, { useEffect, useRef, useState } from 'react';
import Zoom from 'react-medium-image-zoom';

import styled from 'styled-components';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';

import { Color } from '@assets/constants';
import { icons } from '@assets/icons';
import { MugImageSide } from '@containers/adminPanel/utils';
import { getImageAltText, MugSliderImage } from '@containers/Details/utils';
import { ActivityIndicator } from '@ui/ActivityIndicator';

interface Props {
  images: MugSliderImage[];
}

export const MugSwiper = ({ images }: Props) => {
  const swiperRef = useRef<SwiperClass>();
  const [selectedSide, setSelectedSide] = useState<MugImageSide>(
    MugImageSide.FRONT
  );
  const [imageLoadStates, setImageLoadStates] = useState<boolean[]>(
    images.map(() => false)
  );

  const handleImageLoad = (index: number) => {
    setImageLoadStates((prevStates) => {
      const newStates = [...prevStates];
      newStates[index] = true;
      return newStates;
    });
  };

  const handleSlideChange = ({ activeIndex }: SwiperClass) => {
    if (images) {
      setSelectedSide(images[activeIndex].side);
    }
  };

  useEffect(() => {
    const index = images.findIndex((image) => image.side === selectedSide);
    swiperRef.current?.slideTo(index);
  }, [images, selectedSide]);

  return (
    <Swiper
      onSwiper={(swiper: SwiperClass) => {
        swiperRef.current = swiper;
      }}
      onSlideChange={handleSlideChange}
    >
      {images.map((image, index) => (
        <StyledSlide isSingle={images.length === 1} key={index}>
          {!imageLoadStates[index] && (
            <ActivityIndicator size={100} color={Color.ACCENT} />
          )}
          <Zoom
            swipeToUnzoomThreshold={200}
            zoomMargin={-10}
            IconUnzoom={() => <icons.FaTimes size={22} />}
          >
            <Image
              src={image.url}
              alt={getImageAltText(image.name)}
              onLoad={() => handleImageLoad(index)}
              loaded={imageLoadStates[index]}
            />
          </Zoom>
        </StyledSlide>
      ))}
    </Swiper>
  );
};

const Image = styled.img<{ loaded: boolean }>`
  max-width: 100%;
  scale: 0.8;
  transform: translateY(20px);
  ${({ loaded }) => !loaded && 'display: none;'}
`;

const StyledSlide = styled(SwiperSlide)<{ isSingle: boolean }>`
  display: flex;
  justify-content: center;

  ${({ isSingle }) =>
    isSingle &&
    `
      width: 100% !important;
      height: auto !important;
  `}
`;
